import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
} from 'react';

import {
  arrow,
  autoUpdate,
  flip,
  hide,
  offset,
  shift,
  useFloating,
  useClick,
  useDismiss,
  useInteractions,
} from '@floating-ui/react';
import { endOfDay } from 'date-fns';
import type { CSSObject } from 'styled-components';

import { useLocale } from '@adsk/alloy-react-locale';
import theme from '@adsk/alloy-react-theme';

import { DATE_PICKER_VARIANTS } from '../consts';
import { getToday, isPropControlled } from '../helpers';
import type { UseDatePickerCommonArgs } from '../types';

type UseSharedProps = Pick<
  UseDatePickerCommonArgs,
  | 'minDate'
  | 'maxDate'
  | 'disablePastDates'
  | 'disableFutureDates'
  | 'timeZone'
  | 'disabled'
  | 'open'
  | 'initialOpen'
  | 'onHide'
  | 'variant'
  | 'flipArgs'
  | 'shiftArgs'
> & { target?: MutableRefObject<HTMLDivElement> };

const useSharedDateLogic = ({
  minDate: propsMinDate,
  maxDate: propsMaxDate,
  disablePastDates,
  disableFutureDates,
  timeZone,
  disabled,
  open: propsOverlayOpen,
  initialOpen,
  target,
  onHide,
  variant = DATE_PICKER_VARIANTS.FIELD,
  flipArgs,
  shiftArgs,
}: UseSharedProps) => {
  const { T, locale } = useLocale(({ locale: loc }) =>
    require(`../i18n/${loc}.json`),
  );

  const [open, setOpen] = useState(initialOpen);

  const arrowRef = useRef<HTMLDivElement>(null);

  const today = getToday(timeZone);
  const minDate = propsMinDate || (disablePastDates ? today : null);
  const maxDate = propsMaxDate || (disableFutureDates ? endOfDay(today) : null);

  const [referenceElement, setReferenceElement] = useState(target?.current);

  const { refs, context, middlewareData, placement, floatingStyles } =
    useFloating({
      open,
      elements: {
        reference: referenceElement,
      },
      onOpenChange: (x) => {
        if (!x) {
          onHide?.();
        }

        setOpen(x);
      },
      placement:
        variant === DATE_PICKER_VARIANTS.ICON ? 'bottom' : 'bottom-start',
      whileElementsMounted: autoUpdate,
      middleware: [
        offset(variant === DATE_PICKER_VARIANTS.ICON ? 10 : 0),
        flip(flipArgs),
        shift(shiftArgs),
        arrow({
          element: arrowRef,
        }),
        hide({ strategy: 'referenceHidden' }),
      ],
    });

  const click = useClick(context, { enabled: !disabled });

  const dismiss = useDismiss(context);

  const interactions = useInteractions([click, dismiss]);

  const referenceRef = useCallback(
    (node: HTMLDivElement) => {
      if (target) {
        target.current = node;
      }
      setReferenceElement(node);
    },
    [target],
  );

  const toggleOverlay = useCallback(() => {
    if (open) {
      onHide?.();
    }

    setOpen(!open);
  }, [open, onHide]);

  // executes when selecting a single date, or the end date in a range
  const onFinalSelection = useCallback(() => {
    if (!isPropControlled(propsOverlayOpen) && context.open) {
      toggleOverlay();
    }
  }, [context.open, propsOverlayOpen, toggleOverlay]);

  const arrowStyles: CSSObject = useMemo(
    () => ({
      transform: `translate(${Math.round(
        middlewareData.arrow?.x ?? 0,
      )}px,${Math.round(middlewareData.arrow?.y ?? 0)}px)`,
    }),
    [middlewareData.arrow?.x, middlewareData.arrow?.y],
  );

  const arrowProps = useMemo(
    () => ({ ref: arrowRef, style: arrowStyles }),
    [arrowStyles],
  );

  const targetProps = useMemo(
    () => ({
      ref: referenceRef,
      ...interactions.getReferenceProps(),
    }),
    [interactions, referenceRef],
  );

  const overlayProps = useMemo(
    () => ({
      ref: refs.setFloating,
      style: { ...floatingStyles, zIndex: theme.zIndex.dateSelector },
      ...interactions.getFloatingProps(),
    }),
    [floatingStyles, interactions, refs],
  );

  return {
    T,
    locale,
    minDate,
    maxDate,
    open: context.open,
    toggleOverlay,
    placement,
    arrowProps,
    targetProps,
    overlayProps,
    onFinalSelection,
    context,
  };
};

export default useSharedDateLogic;
